// Import extend function.
import Component from '../../../js/core/component.js';
import FormConditions from './c-form-conditions.js';
import FormValidation from './c-form-validation.js';
import FormSummary from './c-form-summary.js';
import FormFormula from './c-form-formula.js';
import FormValueList from './c-form-value-list.js';
import FormAjaxHandler from './c-form-ajax-handler.js';
import getDataSettings from '../../../js/utilities/get-data-settings/u-get-data-settings.js';
import isVisible from '../../../js/utilities/is-visible/u-is-visible.js';

class Form extends Component {

	_defaultSettings() {
		return {
			debug: false
		};
	}

	_init() {
		this.form = this.settings.element;

		this.formContainer = this.form.querySelector('#form-wrapper');
		this.summaryDestination = this.form.querySelector('#form-summary');
		this.errorSummary = this.form.querySelector('#form-error-summary');
		this.requiredElements = document.querySelectorAll('[data-required]');
		this.submitButton = this.form.querySelector('input[type="submit"]');
		this.items = Array.from(this.form.querySelectorAll('[data-control]'));
		this.fileItems = this.form.querySelectorAll('[data-control="file"]');
		this.previewDisabled = this.form.hasAttribute('data-preview-disabled')
		this.fileTotalSizeOk = true;


		/**
         * Disable submit button after submit to prevent duplicates
         */
		this.form.addEventListener('submit', () => {
			this.submitButton.disabled = true;
		});

		/**
         * Toggle required fields initially
         */
		this._toggleRequired();

		/**
         * Initialize form conditions
         */
		if (document.querySelector('#form-conditions')) {
			this.ruleJSON = document.querySelector('#form-conditions').innerHTML;
			this.formConditions = new FormConditions({
				debug: false,
				element: this.element,
				requiredElements: this.requiredElements
			});
			this._initFormConditions();
		}

		/**
         * Initialize form validation
         */
		this.formValidation = new FormValidation({ debug: false });
		this._initFormValidation();

		/**
         * Initialize form summary
         */
		if (!this.previewDisabled) {
			this.formSummary = new FormSummary({ debug: false });
			// Create preview button and append
			this.previewButton = this.formSummary.createPreviewButton();
			this.form.appendChild(this.previewButton);
	
			// Create close summary button
			this.closePreviewButton = this.formSummary.createClosePreviewButton();
			this.form.appendChild(this.closePreviewButton);
		}

		this.toggleSettings = {
			formContainer: this.formContainer,
			summaryDestination: this.summaryDestination,
			errorSummary: this.errorSummary,
			previewButton: this.previewButton,
			closePreviewButton: this.closePreviewButton,
			submitButton: this.submitButton
		};
		this._initFormSummary();

		/**
         * Initialize form formulas
         */
		if (document.querySelector('[data-formula]')) {
			this.formFormula = new FormFormula({ debug: false });
			this._initFormFormula();
		}

		/**
		 * Initialize form ajax
		 */
		if (document.querySelector('[data-ajax-url]')) {
			this.formAjax = new FormAjaxHandler({debug: false});
			this._initFormAjaxHandler();

		}

		/**
         * Initialize form value list
         */
		if (document.querySelector('#value-list')) {
			this.valueListJSON = document.querySelector('#value-list').innerHTML;
			this.formValueList = new FormValueList({ debug: false });
			this._initFormValueList();
		}

		/**
         * Initialize form submit loader indication
         */
		if (document.querySelector('#form-loader')) {
			const loader = document.querySelector('#form-loader');

			this.submitButton.addEventListener('click', () => {
				loader.style.display = 'flex';
			});
		}
	}

	_initFormConditions() {
		if (this.ruleJSON !== null) {
			this.rules = JSON.parse(this.ruleJSON);

			// Run initial rules
			this.formConditions.executeAllRules(this.rules.rules);

			// Setup event listeners and aria-controls on source elements
			this.formConditions.initEventListeners(this.rules.rules);
		}
	}

	_initFormValidation() {
		// Validate each input on blur and remove error on input
		this.items.forEach(item => {
			let controls = Array.from(item.querySelectorAll('input, select, textarea, [data-formula] dd'));

			controls.forEach(control => {
				let isFormula = control.tagName == 'DD' ? true : false;
				let isFile = control.type == 'file' ? true : false;

				if (isFormula) {
					let observer = new MutationObserver((mutations) => {
						mutations.forEach(() => {
							this.formValidation.validateItem(item);
						});
					});

					observer.observe(control, { attributes: false, childList: true, subtree: false });
				}
				else if (isFile) {
					control.addEventListener('input', (event) => {
						this.formValidation.clearError(item);
						this.formValidation.clearTotalFileSizeError(item);
						this.formValidation.validateItem(item, event);
					});
				}
				else {
					control.addEventListener('blur', (event) => {
						this.formValidation.validateItem(item, event);
					});

					control.addEventListener('input', () => {
						this.formValidation.clearError(item);
					});
				}
			});
		});
	}

	_initFormSummary() {
		if (!this.previewDisabled) {
			// Hide submit button
			this.submitButton.style.display = 'none';
			// Disable submit button
			this.submitButton.disabled = true;
			
			// On preview button click generate summary and show
			this.previewButton.addEventListener('click', () => {
				let controlsValidity = this.formValidation.validateItems(this.items);
				this.formValidation.validateTotalFileSize(this.fileItems);
	
				if (controlsValidity.every(Boolean) && this.formValidation.settings.fileTotalSizeOk) {
					let fieldsetsToCheck = this.form.querySelectorAll('fieldset.c-form__fieldset--section');
					let summary = this.formSummary.createSummary(fieldsetsToCheck, this.summaryDestination);
	
					this.summaryDestination.appendChild(summary);
					this.formSummary.toggleFormAndSummary('summary', this.toggleSettings);
	
					this.form.scrollIntoView(true);
				}
				else {
					let firstError = this.form.querySelector('[data-error]');
					let control = firstError.querySelector('input, select, textarea');
					if (firstError.dataset.control !== 'formula') {
						control.focus();
					}
					firstError.scrollIntoView(true);
				}
			});
	
			this.closePreviewButton.addEventListener('click', () => {
				this.formSummary.toggleFormAndSummary('form', this.toggleSettings);
				this.form.scrollIntoView(true);
			});
		}
		this.submitButton.addEventListener('click', () => {
			let controlsValidity = this.formValidation.validateItems(this.items);
			this.formValidation.validateTotalFileSize(this.fileItems);

			if (controlsValidity.every(Boolean) && this.formValidation.settings.fileTotalSizeOk) {

				this.form.scrollIntoView(true);
			}
			else {
				let firstError = this.form.querySelector('[data-error]');
				let control = firstError.querySelector('input, select, textarea');
				if (firstError.dataset.control !== 'formula') {
					control.focus();
				}
				firstError.scrollIntoView(true);
			}
		});

		this.formContainer.setAttribute('aria-live', 'polite');
		this.summaryDestination.setAttribute('aria-live', 'polite');


		window.addEventListener('hashchange', () => {
			if (isVisible(this.closePreviewButton)) {
				this.formSummary.toggleFormAndSummary('form', this.toggleSettings);
			}
		});
	}

	_initFormFormula() {
		this.formulaItems = this.form.querySelectorAll('[data-formula]');

		this.formulaItems.forEach(item => {
			this.formFormula.setupFormula(item);
		});
	}

	_initFormAjaxHandler () {
		this.ajaxItems = this.form.querySelectorAll('[data-ajax-url]');

		this.ajaxItems.forEach(item => {
			const action = item.querySelector('[data-ajax-action]');
			const inputs = Array.from(item.querySelectorAll('[data-ajax-input]'));

			action.addEventListener('click', (e) => {
				e.preventDefault();
				let controlsValidity = this.formValidation.validateItems(inputs);

				if (controlsValidity.every(Boolean)) {
					this.formAjax.init(item);
				}
			});
		});
	}

	_initFormValueList() {
		if (this.valueListJSON !== null) {
			this.valueLists = JSON.parse(this.valueListJSON);
		}

		this.valueLists.forEach(valueList => {
			this.formValueList.initValueList(valueList);
		});
	}

	_toggleRequired() {
		this.requiredElements.forEach(el => {
			let elementIsVisible = isVisible(el);
			if (elementIsVisible && !(el.hasAttribute('required'))) {
				el.setAttribute('required', '');
			}
			else if (!elementIsVisible) {
				el.removeAttribute('required');
			}
		});
	}
}

document.addEventListener('newcontent', (e) => {
	e.target.querySelectorAll('[data-mypages-form]').forEach(element => {
		let settings = getDataSettings(element, {
			debug: false,
			element: element
		});

		element.form = new Form(settings);
	});
});

export default Form;



// WEBPACK FOOTER //
// ./components/form-elements/form/c-form.js